// src/components/support/supportShared.js
//
// Status language, formatting and small pieces shared by the client Support
// page, the admin Support page and the ticket drawer. Status keys come from
// the server (support/service.py STATUSES) - keep the two lists in step.
//
// Colour follows fleet-theme.css: turquoise = being handled / fine,
// neon = needs attention, inverted white chip = urgent. What "needs
// attention" means depends on who is looking: a new ticket needs the team,
// "waiting" needs the client.

import React, { useState } from 'react';

export const STATUS_ORDER = ['new', 'assigned', 'in_progress', 'waiting_client', 'resolved', 'closed'];

const STATUS_META = {
  admin: {
    new:            { label: 'New',              cls: 'ft-badge-degraded' },
    assigned:       { label: 'Assigned',         cls: 'ft-badge-online' },
    in_progress:    { label: 'In progress',      cls: 'ft-badge-online' },
    waiting_client: { label: 'Waiting on client', cls: 'ft-badge-unknown' },
    resolved:       { label: 'Resolved',         cls: 'ft-badge-online' },
    closed:         { label: 'Closed',           cls: 'ft-badge-unknown' },
  },
  client: {
    new:            { label: 'Received',         cls: 'ft-badge-online' },
    assigned:       { label: 'Assigned',         cls: 'ft-badge-online' },
    in_progress:    { label: 'In progress',      cls: 'ft-badge-online' },
    waiting_client: { label: 'Waiting for you',  cls: 'ft-badge-degraded' },
    resolved:       { label: 'Resolved',         cls: 'ft-badge-online' },
    closed:         { label: 'Closed',           cls: 'ft-badge-unknown' },
  },
};

export const statusLabel = (status, scope) => (STATUS_META[scope][status] || { label: status }).label;

export function TicketStatus({ status, scope }) {
  const meta = STATUS_META[scope][status] || { label: status, cls: 'ft-badge-unknown' };
  return <span className={`ft-badge ${meta.cls}`}>{meta.label}</span>;
}

export const PRIORITIES = [
  { id: 'low', label: 'Low', hint: 'A question, no rush' },
  { id: 'normal', label: 'Normal', hint: 'Something isn’t right' },
  { id: 'high', label: 'High', hint: 'Working badly, affecting crew or guests' },
  { id: 'urgent', label: 'Urgent', hint: 'Vessel has no internet at all' },
];

export function PriorityTag({ priority }) {
  if (priority === 'urgent') return <span className="ft-badge ft-badge-down">Urgent</span>;
  if (priority === 'high') return <span className="sp-prio sp-prio-high">High</span>;
  if (priority === 'low') return <span className="sp-prio">Low</span>;
  return null; // normal: say nothing
}

// Server timestamps are UTC "YYYY-MM-DD HH:MM:SS".
export function parseTs(ts) {
  if (!ts) return null;
  const d = new Date(String(ts).replace(' ', 'T') + (String(ts).includes('Z') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function timeAgo(ts) {
  const d = parseTs(ts);
  if (!d) return '—';
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatStamp(ts) {
  const d = parseTs(ts);
  if (!d) return '—';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Phones take 4000px photos; nobody needs that to see a cable. Downscale to
// JPEG so three photos stay well under the upload limit on a vessel link.
export const MAX_IMAGES = 3;
export async function imageToDataUrl(file, maxSide = 1600, quality = 0.82) {
  if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|heic)$/i.test(file.name)) {
    throw new Error('Only images can be attached.');
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error(`Couldn't read ${file.name}.`));
      i.src = url;
    });
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // transparent PNG areas would turn black in JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    return { name: file.name.replace(/\.[^.]+$/, '') + '.jpg', data: dataUrl };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Thumbnails + a full-screen viewer. Images are served with the session
// cookie, so a plain <img src> works in the browser and the app alike.
export function AttachmentStrip({ items, base }) {
  const [open, setOpen] = useState(null);
  if (!items?.length) return null;
  return (
    <>
      <div className="sp-atts">
        {items.map((a) => (a.removed ? (
          // Deleted by the photo retention rule (Settings > Support).
          <span key={a.id} className="sp-att sp-att-removed" title="Photo removed after the ticket was closed">
            <i className="fas fa-image" aria-hidden="true"></i>
            <span>Removed</span>
          </span>
        ) : (
          <button key={a.id} type="button" className="sp-att" onClick={() => setOpen(a)} aria-label={`View ${a.name}`}>
            <img src={`${base}/${a.id}`} alt={a.name} loading="lazy" />
          </button>
        )))}
      </div>
      {open && (
        <div className="sp-lightbox" role="dialog" aria-label={open.name} onClick={() => setOpen(null)}>
          <button type="button" className="bl-icon-btn sp-lightbox-close" aria-label="Close"><i className="fas fa-times"></i></button>
          <img src={`${base}/${open.id}`} alt={open.name} />
        </div>
      )}
    </>
  );
}
